import type {Status} from '@/modules/common/context/Consent/models';

import Cookies from 'js-cookie';
import {useEffect, useState, useMemo} from 'react';

import {ConsentContext} from '@/modules/common/context/Consent/ConsentContext';

// Reads the consent cookie to get the status
const getConsentStatus = () => {
  // This cookie will be set after the user has made a decision and contains the consent status.
  const cookieConsentCookie = Cookies.get('CookieConsent');
  // No consent yet.
  if (!cookieConsentCookie) {
    return null;
  }
  // For whatever reason this isn't stored as JSON but as JS-Object string...
  // Example: "{stamp:'qyLprJPNejG53QyVZfZl6clQYCO7ae8JC3WsO7RX1FqVGKtQ0H88iA==',necessary:true,preferences:false,statistics:false,marketing:false,method:'explicit',ver:1,utc:1698420236319,region:'de'}"
  const consentStatus = Object.fromEntries(
    cookieConsentCookie
      .replaceAll(/[{}]/gu, '')
      .split(',')
      .map((keyValuePair) => keyValuePair.split(':'))
  );

  return {
    consented: consentStatus.necessary === 'true',
    declined: consentStatus.necessary === 'false',
    necessary: consentStatus.necessary === 'true',
    preferences: consentStatus.preferences === 'true',
    statistics: consentStatus.statistics === 'true',
    marketing: consentStatus.marketing === 'true',
  };
};

const POLLING_INTERVAL = 250;
const TIMEOUT = 60_000;

type Props = React.PropsWithChildren;

export const ConsentProvider: React.FunctionComponent<Props> = ({children}) => {
  const [status, setStatus] = useState<Status | null>(() => getConsentStatus());

  useEffect(() => {
    let intervalId: number;
    let timeoutId: number;
    // Start polling until we know the consent status.
    // If it takes to long we stop checking and assume no consent.
    if (!status) {
      intervalId = window.setInterval(() => {
        const currentStatus = getConsentStatus();
        if (currentStatus) {
          setStatus(currentStatus);
          window.clearInterval(intervalId);
        }
      }, POLLING_INTERVAL);
      timeoutId = window.setTimeout(() => {
        window.clearInterval(intervalId);
      }, TIMEOUT);
    }
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [status]);

  const contextValue = useMemo(
    () => ({
      status,
    }),
    [status]
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};
